import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCan, useGetUserInfoQuery } from '@features/user';
import { Alert, Box, Button, Container, Typography } from '@mui/material';

interface RecordingPermissionGateProps {
  children: ReactNode;
}

// TODO(gdn-2610): remove once the allowlisted org's recording staff are
// migrated to the clinical_staff role. Transitional fallback only — their
// admin-role users record today without `record_encounter_inputs`.
const ADMIN_RECORDING_ALLOWLIST = [
  '6ba543ea-de84-4052-a289-b28282cd5f49',
];

export const RecordingPermissionGate = ({ children }: RecordingPermissionGateProps) => {
  const navigate = useNavigate();
  const { data: userInfo } = useGetUserInfoQuery(undefined);

  // ADR 0073 / GDN-2610: recording is a permission
  // (`record_encounter_inputs`, granted to clinician and clinical_staff),
  // not a role check.
  const canRecord = useCan('record_encounter_inputs');

  const isInAllowlistedGroup = (userInfo?.group_ids || []).some(groupId =>
    ADMIN_RECORDING_ALLOWLIST.includes(groupId),
  );

  // Default-allow while user info is loading (matches the previous gate) —
  // only block once we know the user lacks the permission.
  const shouldBlockRecording = !!userInfo && !canRecord && !isInAllowlistedGroup;

  if (shouldBlockRecording) {
    return (
      <Container maxWidth={"sm"}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 3,
          }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Access Restricted
            </Typography>
            <Typography variant="body2">
              This account does not have recording permissions. Please contact your organization
              administrator if you believe this is a mistake.
            </Typography>
          </Alert>

          <Button variant="contained" onClick={() => navigate('/dashboard')} sx={{ mt: 2 }}>
            Go to Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  // User has permission, render children
  return <>{children}</>;
};
